import {
  FormTemplate,
  FormTemplateSection,
  Template,
  RawTemplate,
} from 'data/template/template.types';
import { ValueLabelPair } from 'data/types';
import { Warrant, WARRANT_LEA_ANY } from 'data/warrant/warrant.types';
import cloneDeep from 'lodash.clonedeep';
import { hideAttachmentFieldFromTemplate } from 'xcipio/warrants/forms';
import CaseCreateOptions from 'xcipio/cases/options/case-options.json';
import { checkTimeValidationInComputerTimezone } from 'shared/constants/checkTimeValidation';
import { Team } from 'data/team/team.types';
import { CollectionFunctionGroup } from 'data/collectionFunctionGroup/collectionFunctionGroup.types';
import { CONFIG_FORM_MODE } from 'shared/popup/config-form-popup';
import { getSortedTimeZoneValueLabels } from 'shared/timeZoneUtils/timeZone.utils';
import i18n from 'i18n';
import { CollectionFunction } from 'data/collectionFunction/collectionFunction.types';
import { QUERY_LOC_FROM_AMF_ID, DEPLOYMENT_TUNISIA, CASE_OPT_WITH_LOCATION } from 'xcipio/constants';
import { Contact } from 'data/contact/contact.types';
import template from 'lodash.template';

const defaultOption = {
  label: i18n.t('warrant.form.fieldOption.select'),
  value: '',
};

export function adaptTemplate(
  formTemplate: Template,
  mode: string,
  warrant: Warrant | null,
  templates: RawTemplate[],
  attachmentEnabled: boolean,
  timezones: ValueLabelPair[],
  contacts: Contact[],
  teams: Team[],
  divisions: { [key: string]: string; },
  deployment: string,
  collectionFunctionGroups: CollectionFunctionGroup[],
  collectionFunctions: CollectionFunction[],
  casesCollectionFunctionIds: string[],
  handleManageAttachmentClick: () => void,
  showCaseOptions: () => void,
  showTargetOptions: () => void,
  visibilityScope: VisibilityScope = noVisibilityScope,
): Template {
  const clonedFormTemplate = cloneDeep(formTemplate);

  const filteredCFGroups = collectionFunctionGroups.filter((cfg) => cfg.cfs.length > 0);

  const leaOptions = getLeaOptions(
    mode,
    warrant,
    filteredCFGroups,
    collectionFunctions,
    casesCollectionFunctionIds,
  );

  setTimezoneOptions(clonedFormTemplate.template, warrant, timezones);
  setDivisionOptions(clonedFormTemplate.template, warrant, divisions);
  setContactOptions(clonedFormTemplate.template, contacts);
  setVisibilityOptions(clonedFormTemplate.template, teams, warrant, mode, visibilityScope);
  setWarrantLEAOptions(clonedFormTemplate.template, leaOptions);
  setCaseTypes(
    clonedFormTemplate.template,
    templates,
  );
  setCaseOptions(
    clonedFormTemplate.template, templates,
    warrant?.caseType,
  );

  hideAttachmentFieldFromTemplate(clonedFormTemplate.template, attachmentEnabled ?? false);

  setFormTemplateFieldReadOnly(clonedFormTemplate, warrant);

  setManageAttachmentHandler(clonedFormTemplate.template, warrant, handleManageAttachmentClick);
  setCaseOptionDetailsHandler(clonedFormTemplate.template, showCaseOptions);
  setTargetOptionDetailsHandler(clonedFormTemplate.template, showTargetOptions, warrant);

  adaptTemplateForCurrentDeployment(clonedFormTemplate.template, warrant, deployment)
  return clonedFormTemplate;
}

export function setTimezoneOptions(
  formTemplate: FormTemplate,
  warrant: Warrant | null,
  timezones: ValueLabelPair[],
): void {
  if (timezones.length > 0) {
    const formTemplateField = (formTemplate.general as FormTemplateSection).fields.timeZone;

    formTemplateField.type = 'select';

    const date = new Date();
    const newTimeZones = [...timezones];

    // if the warrant.timezone is not in the list of timezones from the DB, add to the time zone list.
    const matchWarrantTimeZone = timezones.some((timezone) => (timezone.value === warrant?.timeZone));
    if (warrant && warrant.timeZone && !matchWarrantTimeZone) {
      newTimeZones.push({
        label: warrant.timeZone,
        value: warrant.timeZone,
      });
    }

    // Add offsets to label and sort the labels
    const timezoneOptions = getSortedTimeZoneValueLabels(date, newTimeZones);

    if (timezoneOptions.length === 1) {
      formTemplateField.initial = timezoneOptions[0].value;
    }

    if (formTemplateField.options == null) {
      formTemplateField.options = [];
    }

    formTemplateField.options?.push(...timezoneOptions);
  }
}

export function setDivisionOptions(
  formTemplate: FormTemplate,
  warrant: Warrant | null,
  divisions: { [key: string]: string; },
): void {
  const formTemplateField = (formTemplate.general as FormTemplateSection).fields?.division;
  let divisionOptions: ValueLabelPair[] = [];

  if (Object.keys(divisions).length > 0 && formTemplateField) {
    formTemplateField.type = 'select';

    Object.entries(divisions).map(([key, value]) => divisionOptions.push({ label: value, value }));

    if (divisionOptions.length === 1) {
      formTemplateField.initial = divisionOptions[0].value;
    }

    if (formTemplateField.options == null) {
      formTemplateField.options = [];
    }
  }

  if (warrant && warrant.division) {
    divisionOptions = [{ label: warrant.division, value: warrant.division }]
  }

  if (formTemplateField) {
    formTemplateField.options?.push(...divisionOptions);
  }
}

export function adaptTemplateLeaField(
  formTemplate: Template,
  disabled: boolean,
): Template {
  const clonedFormTemplate = cloneDeep(formTemplate);

  (clonedFormTemplate.template.general as FormTemplateSection).fields.lea.disabled = disabled;

  return clonedFormTemplate;
}

export function adaptTemplateDivisionField(
  formTemplate: Template,
  isSinglepointprovisioningEnabled: boolean,
): Template {
  const clonedFormTemplate = cloneDeep(formTemplate);

  const divisionField = (clonedFormTemplate.template.general as FormTemplateSection).fields?.division;
  if (divisionField) {
    divisionField.hide = isSinglepointprovisioningEnabled ? 'false' : 'true';
  }

  return clonedFormTemplate;
}

export function adaptTemplateTargetOptions(
  formTemplate: Template,
  templates: RawTemplate[],
  mode: string
): Template {
  const clonedFormTemplate = cloneDeep(formTemplate);

  if (
    (clonedFormTemplate.template.general as FormTemplateSection).fields.targetOptionDetails &&
    templates.some((template) => JSON.parse(template.template).key === 'caseOptionsQueryLocationFromAmf' && mode !== CONFIG_FORM_MODE.Edit)
  ) {
    (clonedFormTemplate.template.general as FormTemplateSection).fields.targetOptionDetails.hide = 'false';
  }

  return clonedFormTemplate;
}

export function adaptInterceptTypeOptions(
  formTemplate: Template,
  isNokamflcsimEnabled: boolean,
  isDisplayLocDataInInterceptDropDownEnabled: boolean
): Template {
  const clonedFormTemplate = cloneDeep(formTemplate);

  if (!isNokamflcsimEnabled) {
    const interceptTypeOptions = (clonedFormTemplate.template.general as FormTemplateSection)?.fields?.caseType?.options;

    if (interceptTypeOptions) {
      (clonedFormTemplate.template.general as FormTemplateSection).fields.caseType.options = interceptTypeOptions.filter((option) => option.value !== 'QUERY_LOC_FROM_AMF');
    }
  }

  if (!isDisplayLocDataInInterceptDropDownEnabled) {
    const interceptTypeOptions = (clonedFormTemplate.template.general as FormTemplateSection)?.fields?.caseType?.options;

    if (interceptTypeOptions) {
      (clonedFormTemplate.template.general as FormTemplateSection).fields.caseType.options = interceptTypeOptions.filter((option) => option.value !== 'LOC_ONLY_IN_IRI');
    }
  }

  return clonedFormTemplate;
}

function setContactOptions(formTemplate: FormTemplate, contacts: Contact[]) {
  const contactOptions = contacts.map((contact) => {
    return {
      label: contact.name,
      value: contact.id,
    };
  });
  contactOptions.unshift(defaultOption);
  (formTemplate.general as FormTemplateSection).fields.contact.options = contactOptions;
}

/** The "team" the backend appends to the user's teams for a warrant that every team can see. */
const UNRESTRICTED_TEAM_ID = '';
const UNRESTRICTED_TEAM_NAME = 'Unrestricted';

export type VisibilityScope = {
  isMultitenantEnabled: boolean,
  isSystemAdmin: boolean,
  /** Every team of the system (GET /teams); the user's own teams do not include them all. */
  allTeams: Team[],
};

const noVisibilityScope: VisibilityScope = { isMultitenantEnabled: false, isSystemAdmin: false, allTeams: [] };

/**
 * Under multitenant only a system administrator can make a warrant Unrestricted, and can
 * give it to any team; everyone else only gets their own teams.
 * The current owner of an existing warrant is always kept so that it still shows.
 */
function getVisibilityTeams(
  teams: Team[],
  warrant: Warrant | null,
  mode: string,
  scope: VisibilityScope,
): Team[] {
  if (!scope.isMultitenantEnabled) {
    return teams;
  }

  const unrestricted = teams.find(({ id }) => id === UNRESTRICTED_TEAM_ID)
    ?? { id: UNRESTRICTED_TEAM_ID, name: UNRESTRICTED_TEAM_NAME };
  const ownTeams = teams.filter(({ id }) => id !== UNRESTRICTED_TEAM_ID);

  let visibilityTeams = ownTeams;
  if (scope.isSystemAdmin) {
    const systemTeams = scope.allTeams.length > 0 ? scope.allTeams : ownTeams;
    visibilityTeams = [
      ...[...systemTeams].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
      unrestricted,
    ];
  }

  const isExistingWarrant = mode === CONFIG_FORM_MODE.Edit || mode === CONFIG_FORM_MODE.View;
  const currentOwner = warrant?.dxOwner;
  if (isExistingWarrant && currentOwner != null && !visibilityTeams.some(({ id }) => id === currentOwner)) {
    const currentTeam = [...teams, ...scope.allTeams, unrestricted].find(({ id }) => id === currentOwner);
    visibilityTeams = [...visibilityTeams, currentTeam ?? { id: currentOwner, name: currentOwner }];
  }

  return visibilityTeams;
}

function setVisibilityOptions(
  formTemplate: FormTemplate,
  teams: Team[],
  warrant: Warrant | null,
  mode: string,
  scope: VisibilityScope,
) {
  const visibilityOptions = getVisibilityTeams(teams, warrant, mode, scope).map((team) => {
    return {
      label: team.name,
      value: team.id,
    };
  });
  (formTemplate.general as FormTemplateSection).fields.dxOwner.options = visibilityOptions;
  (formTemplate.general as FormTemplateSection).fields.dxOwner.initial = visibilityOptions.length >= 1 ? visibilityOptions[0].value : '';
}

/** Rebuilds only the Visibility options, for when the teams arrive after the form was built. */
export function adaptTemplateVisibilityOptions(
  formTemplate: Template,
  mode: string,
  warrant: Warrant | null,
  teams: Team[],
  scope: VisibilityScope,
): Template {
  const clonedFormTemplate = cloneDeep(formTemplate);
  setVisibilityOptions(clonedFormTemplate.template, teams, warrant, mode, scope);
  return clonedFormTemplate;
}

function setManageAttachmentHandler(
  formTemplate: FormTemplate,
  warrant: Warrant | null,
  handleManageAttachmentClick: () => void,
) {
  if ((formTemplate.general as FormTemplateSection).fields.attachmentsDetails) {
    let attachmentsLength = 0;
    if (warrant && warrant.attachments) {
      attachmentsLength = warrant.attachments.length;
    }
    (formTemplate.general as FormTemplateSection).fields.attachmentsDetails.handleClick = handleManageAttachmentClick;
    (formTemplate.general as FormTemplateSection).fields.attachmentsDetails.initial = `${attachmentsLength} Attachments`;
  }
}

function getLeaOptions(
  mode: string,
  warrant: Warrant | null,
  collectionFunctionGroups: CollectionFunctionGroup[],
  collectionFunctions: CollectionFunction[],
  casesCollectionFunctionIds: string[],
) {
  if (mode !== CONFIG_FORM_MODE.Edit || warrant == null) {
    return collectionFunctionGroups;
  }

  const casesCollectionFunctionTags = casesCollectionFunctionIds.map((id) => {
    return collectionFunctions.find(({ id: entryId }) => id === entryId)?.tag ?? '';
  });

  return collectionFunctionGroups.filter(({ cfs }) => {
    const collectionFunctionGroupTags: string[] = cfs.map(({ id }) => {
      return collectionFunctions.find(({ id: entryId }) => id === entryId)?.tag ?? '';
    });

    return casesCollectionFunctionTags.every((tag) => collectionFunctionGroupTags.includes(tag));
  });
}

function setWarrantLEAOptions(formTemplate: FormTemplate, cfGroups: CollectionFunctionGroup[]) {
  const warrantFormTemplate = formTemplate;
  const leaOptionsUnsorted = cfGroups.map((item) => {
    return {
      label: item.name,
      value: item.id,
    };
  });
  const leaOptions = leaOptionsUnsorted.sort((a, b) => {
    const fa = a.label.toLowerCase();
    const fb = b.label.toLowerCase();

    if (fa < fb) {
      return -1;
    }
    if (fa > fb) {
      return 1;
    }
    return 0;
  });
  leaOptions.unshift(defaultOption);
  leaOptions.push({ label: i18n.t('warrant.form.fieldOption.lea.any'), value: WARRANT_LEA_ANY });
  if ((warrantFormTemplate.general as FormTemplateSection).fields.lea) {
    (warrantFormTemplate.general as FormTemplateSection).fields.lea.options = leaOptions;
  }
}

function setCaseTypes(
  formTemplate: FormTemplate,
  templates: RawTemplate[],
): void {
  const hasLocationDataOnlyTemplate = templates.some((template) => template.name === 'Location Data Only');
  const hasQueryLocationFromAmfTemplate = templates.some((template) => template.name === 'Query Location From AMF');
  if (!hasLocationDataOnlyTemplate) {
    const caseTypesOptions = (formTemplate.general as FormTemplateSection).fields.caseType.options;
    (formTemplate.general as FormTemplateSection).fields.caseType.options = caseTypesOptions?.filter((option) => option.value !== 'LOC_ONLY_IN_IRI');
  }

  if (!hasQueryLocationFromAmfTemplate && formTemplate.key === 'wizardWarrantTemplateAndromeda') {
    const caseTypesOptions = (formTemplate.general as FormTemplateSection).fields.caseType.options;
    (formTemplate.general as FormTemplateSection).fields.caseType.options = caseTypesOptions?.filter((option) => option.value !== 'QUERY_LOC_FROM_AMF');
  }
}

export function setCaseOptions(
  formTemplate: FormTemplate,
  templates: RawTemplate[],
  warrantCaseType?: string,
): void {
  if ((formTemplate.general as FormTemplateSection).fields.caseOptTemplateUsed) {
    (formTemplate.general as FormTemplateSection).fields.caseOptTemplateUsed.options
    = getAllCaseOptionTemplates(templates, warrantCaseType);
  }
}

function setCaseOptionDetailsHandler(formTemplate: FormTemplate, showCaseOptions: () => void): void {
  if ((formTemplate.general as FormTemplateSection).fields.caseOptionDetails) {
    (formTemplate.general as FormTemplateSection).fields.caseOptionDetails.handleClick = showCaseOptions;
  }
}

function setTargetOptionDetailsHandler(formTemplate: FormTemplate, showTargetOptions: () => void, warrant: Warrant | null): void {
  if (warrant?.caseType === 'LOC_ONLY_IN_IRI' && warrant?.caseOptTemplateUsed) {
    setTargetOptionsReadOnly(formTemplate, warrant?.caseOptTemplateUsed);
  }
  if ((formTemplate.general as FormTemplateSection).fields.targetOptionDetails) {
    (formTemplate.general as FormTemplateSection).fields.targetOptionDetails.handleClick = showTargetOptions;
  }
}

function getAllCaseOptionTemplates(
  templates: RawTemplate[],
  warrantCaseType?: string,
) {
  if (templates.length === 0) {
    templates = CaseCreateOptions as RawTemplate[];
  }
  const result = templates
    .filter((template) => template.category === 'CaseOptions')
    .filter((template) => {

      const templateObject = cloneDeep(JSON.parse(template.template));

      if (warrantCaseType === 'LOC_ONLY_IN_IRI') {
        return [
          'caseOptionsLocationOnly',
        ].includes(templateObject.key);
      }

      if (warrantCaseType === 'QUERY_LOC_FROM_AMF') {
        return [
          'caseOptionsQueryLocationFromAmf',
        ].includes(templateObject.key);
      }

      return ![
        'caseOptionsLocationOnly',
        'caseOptionsQueryLocationFromAmf',
      ].includes(templateObject.key);

    })
    .map((template) => ({ label: template.name, value: template.id }));

  result.unshift(defaultOption);
  return result;
}

function setFormTemplateFieldReadOnly(formTemplate: Template, warrant: Warrant | null) {
  if (warrant != null && checkTimeValidationInComputerTimezone(warrant.startDateTime, warrant.stopDateTime)) {
    (formTemplate.template.general as FormTemplateSection).fields.startDateTime.disabled = true;
    (formTemplate.template.general as FormTemplateSection).fields.timeZone.disabled = true;
    const divisionField = (formTemplate.template.general as FormTemplateSection).fields?.division;
    if (divisionField) {
      divisionField.disabled = true;
    }
  }
}

export function setTargetOptionsReadOnly(formTemplate: FormTemplate, warrantCaseType: string): void {
  if ((formTemplate.general as FormTemplateSection).fields.targetOptionDetails == null) {
    return;
  }

  if (warrantCaseType === QUERY_LOC_FROM_AMF_ID) {
    (formTemplate.general as FormTemplateSection).fields.targetOptionDetails.readOnly = false;
    (formTemplate.general as FormTemplateSection).fields.targetOptionDetails.disabled = false;
  } else {
    (formTemplate.general as FormTemplateSection).fields.targetOptionDetails.readOnly = true;
  }
}

function adaptTemplateForCurrentDeployment(formTemplate: FormTemplate, warrant: Warrant | null, deployment: string) {
  if (deployment !== DEPLOYMENT_TUNISIA) {
    return;
  }
  const formSection = (formTemplate.general as FormTemplateSection);
  const withLocation = (formSection.fields.caseOptTemplateUsed.options ?? [])?.find((o) => o.label === 'With Location');
  if (withLocation) {
    formSection.fields.caseOptTemplateUsed.initial = withLocation.value;
    formSection.fields.caseOptTemplateUsed.disabled = true;
  }

  const firstLea = (formSection.fields.lea.options ?? [])?.find((o) => o.value !== '' && o.value !== 'Any');
  if (firstLea) {
    formSection.fields.lea.initial = firstLea.value;
  }
}
